/* eslint-disable import/prefer-default-export */
// eslint-disable-next-line import/no-extraneous-dependencies
import React from 'react';

/**
 * Creates a ref with a particular name.
 * The name doesn't actually do anything, but is used to help testing.
 *
 * @template T
 * @param {string} name ignored, used for testing
 * @param {T} initialValue
 */
export function useNamedRef(name, initialValue) {
  return React.useRef(initialValue);
}

function windowSize() {
  const width =
    window.innerWidth ||
    document.documentElement.clientWidth ||
    document.body.clientWidth;
  const height =
    window.innerHeight ||
    document.documentElement.clientHeight ||
    document.body.clientHeight;
  return { width, height };
}

/**
 * Debounce events that trigger too fast.
 *
 * @param {() => void} fn event handler
 * @param {number} timeout minimum time between calls to `fn`
 * @param {boolean} finish should `fn` be called one final time?
 * @return {() => void} debounced event handler
 */
function debounce(fn, timeout, finish = false) {
  // A lock to determine if `fn` can run
  let canRun = true;
  // Were any events locked out?
  let lostEvents = false;

  return function ret() {
    if (!canRun) {
      lostEvents = true;
      return;
    }
    fn();

    // Lock `fn`
    canRun = false;
    lostEvents = false;

    // Unlock in `timeout` milliseconds
    setTimeout(() => {
      canRun = true;
      // If any events were dropped because `fn` was locked, call `fn` again.
      if (finish && lostEvents) ret();
    }, timeout);
  };
}

/**
 * React hook to capture the window size, and automatically update the size
 * during resize events.
 *
 * For performance reasons, resize events are debounced. You can set the timeout
 * for this debouncing via the `updateTimeout` parameter.
 * After the user finishes resizing, one last resize event gets triggered to
 * avoid obvious problems when the user resizes too quickly. This can be
 * disabled via the `finish` parameter.
 *
 * You can stop watching for resize events via the `disable` method, and
 * resume watching for resize events via the `enable` method.
 *
 * @param {number} updateTimeout refresh time for resize events
 * @param {boolean} finish should one last resize event be triggered after the
 * user finishes input?
 *
 * @returns {{
 *  width: number,
 *  height: number,
 *  enable: () => void,
 *  disable: () => void,
 * }}
 */
export function useWindowSize(updateTimeout = 500, finish = true) {
  const [size, setSize] = React.useState(windowSize());

  let enabled = false;
  const onresize = debounce(() => {
    setSize(windowSize());
  }, updateTimeout, finish);

  function enable() {
    if (enabled) return;
    window.addEventListener('resize', onresize);
    onresize();
    enabled = true;
  }

  function disable() {
    if (!enabled) return;
    window.removeEventListener('resize', onresize);
    enabled = false;
  }

  React.useEffect(() => {
    enable();
    return disable;
  }, []);

  return { ...size, enable, disable };
}
